import { animate, hover, press } from 'motion';

/**
 * Premium micro-interactions layer, built on Motion (motion.dev).
 *
 * Deliberately separate from the GSAP/ScrollTrigger story engine in
 * main.js — this file never touches scroll or the 3D rig. It only owns
 * discrete, pointer-driven interactions: a magnetic custom cursor,
 * magnetic buttons, spring press/hover feedback, and a sliding selection
 * indicator. Skipped entirely on touch devices and prefers-reduced-motion.
 */

const SPRING = { type: 'spring', stiffness: 300, damping: 24, mass: 0.6 };
const SPRING_SOFT = { type: 'spring', stiffness: 220, damping: 26, mass: 0.7 };
const SPRING_SNAPPY = { type: 'spring', stiffness: 420, damping: 28 };

export function initMotionInteractions() {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer: fine)').matches;
  if (reducedMotion) return;

  if (finePointer) {
    initCustomCursor();
    initMagneticButtons();
  }
  initPressFeedback();
  initHoverLift();
  initSizeSelectorPill();
}

/* ------------------------------------------------------------------
   Custom cursor — a small ring that trails the pointer with spring
   physics, and a dot that morphs to a larger, filled state over
   interactive elements. Purely decorative, pointer-events: none.
   ------------------------------------------------------------------ */
function initCustomCursor() {
  const ring = document.createElement('div');
  ring.className = 'cc-cursor-ring';
  const dot = document.createElement('div');
  dot.className = 'cc-cursor-dot';
  document.body.append(ring, dot);

  let shown = false;
  window.addEventListener('pointermove', (e) => {
    if (!shown) {
      animate(ring, { opacity: 1 }, { duration: 0.3 });
      animate(dot, { opacity: 1 }, { duration: 0.3 });
      shown = true;
    }
    animate(ring, { x: e.clientX, y: e.clientY }, SPRING_SOFT);
    animate(dot, { x: e.clientX, y: e.clientY }, { type: 'spring', stiffness: 700, damping: 40 });
  });

  document.querySelectorAll('a, button, [data-cursor-hover]').forEach((el) => {
    hover(el, () => {
      animate(ring, { scale: 2.2, opacity: 0.5 }, SPRING);
      animate(dot, { scale: 0 }, SPRING);
      return () => {
        animate(ring, { scale: 1, opacity: 1 }, SPRING);
        animate(dot, { scale: 1 }, SPRING);
      };
    });
  });

  window.addEventListener('pointerleave', () => {
    animate(ring, { opacity: 0 }, { duration: 0.2 });
    animate(dot, { opacity: 0 }, { duration: 0.2 });
    shown = false;
  });
}

/* ------------------------------------------------------------------
   Magnetic buttons — nudge toward the cursor within their own bounds,
   spring back to rest on leave. Applied to primary CTAs only (not
   every link) so it reads as a deliberate, premium detail.
   ------------------------------------------------------------------ */
function initMagneticButtons() {
  document.querySelectorAll('.btn, .nav__cta').forEach((el) => {
    const strength = 0.35;
    el.addEventListener('pointermove', (e) => {
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - (rect.left + rect.width / 2);
      const dy = e.clientY - (rect.top + rect.height / 2);
      animate(el, { x: dx * strength, y: dy * strength }, SPRING);
    });
    el.addEventListener('pointerleave', () => {
      animate(el, { x: 0, y: 0 }, SPRING);
    });
  });
}

/* ------------------------------------------------------------------
   Press feedback — a tactile spring squash on every button-like
   element, independent of pointer type (works on touch too).
   ------------------------------------------------------------------ */
function initPressFeedback() {
  press('.btn, .nav__cta, .size-selector__btn, .product-card__cta', (el) => {
    animate(el, { scale: 0.94 }, SPRING_SNAPPY);
    return () => animate(el, { scale: 1 }, SPRING_SNAPPY);
  });
}

/* ------------------------------------------------------------------
   Hover lift — cards and gallery tiles rise slightly with a soft
   spring, reinforcing depth without a heavy shadow system.
   ------------------------------------------------------------------ */
function initHoverLift() {
  hover('.info-card, .gallery-grid__item, .buy__card', (el) => {
    animate(el, { y: -8 }, SPRING_SOFT);
    return () => animate(el, { y: 0 }, SPRING_SOFT);
  });
}

/* ------------------------------------------------------------------
   Größenwahl — a pill background that springs to whichever size is
   selected, instead of an instant class swap.
   ------------------------------------------------------------------ */
function initSizeSelectorPill() {
  const group = document.querySelector('.size-selector');
  if (!group) return;

  const pill = document.createElement('div');
  pill.className = 'size-selector__pill';
  pill.setAttribute('aria-hidden', 'true');
  group.prepend(pill);

  const buttons = () => Array.from(group.querySelectorAll('.size-selector__btn'));

  function movePillTo(btn, animated = true) {
    const groupRect = group.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const x = btnRect.left - groupRect.left;
    const target = { x, width: btnRect.width, height: btnRect.height };
    if (animated) animate(pill, target, SPRING_SNAPPY);
    else Object.assign(pill.style, { transform: `translateX(${x}px)`, width: `${target.width}px`, height: `${target.height}px` });
  }

  const active = group.querySelector('.size-selector__btn--active') || buttons()[0];
  if (active) requestAnimationFrame(() => movePillTo(active, false));

  buttons().forEach((btn) => {
    btn.addEventListener('click', () => movePillTo(btn));
  });

  window.addEventListener('resize', () => {
    const current = group.querySelector('.size-selector__btn--active');
    if (current) movePillTo(current, false);
  });
}
